import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
  errors?: string[];
}

interface ImportStats {
  totalRecords: number;
  uuidMatches: number;
  nameMatches: number;
  notFound: number;
  duplicates: number;
  totalAmount: number;
  accuracy: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
}

interface DuplicateRecord {
  driverName: string;
  driverUuid: string;
  existingDate: string;
  newDate: string;
  amount: number;
}

export const UberImportScreen = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 5,
    message: 'Ready to import Uber data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Calculate SHA-256 hash of file content for duplicate detection
  const calculateFileHash = async (fileContent: string): Promise<string> => {
    try {
      if (Platform.OS === 'web') {
        // Use Web Crypto API for browsers
        const encoder = new TextEncoder();
        const data = encoder.encode(fileContent);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } else {
        // For mobile platforms, use a simple hash function
        let hash = 0;
        for (let i = 0; i < fileContent.length; i++) {
          const char = fileContent.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
      }
    } catch (error) {
      console.warn('Hash calculation failed, using timestamp fallback:', error);
      return Date.now().toString(16);
    }
  };

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Uber' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'UUID Matching', description: 'Match drivers using Uber UUIDs' },
    { id: 4, name: 'Data Processing', description: 'Process transactions and earnings' },
    { id: 5, name: 'Database Update', description: 'Save data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 5,
          message: `Selected: ${result.assets[0].name}`,
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performRealImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    try {
      setProgress({ phase: 'uploading', currentStep: 1, totalSteps: 5, message: 'Reading file...' });
      
      // Read the file content using web-compatible method
      let content: string;
      if (selectedFile.uri) {
        // For web, we use fetch to read the blob
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        // Fallback: if we have a File object directly
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      // Remove BOM if present (Uber files often have BOM)
      if (content.charCodeAt(0) === 0xFEFF) {
        content = content.slice(1);
      }
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Validating file format...',
        details: 'Checking CSV structure and headers'
      });

      // Parse CSV
      const lines = content.split('\n');
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('CSV Headers:', headers);

      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 5, 
          message: 'Checking for duplicate data...',
          details: 'Verifying transaction dates against existing records (checking first 100 records)'
        });

        // Check for duplicates with timeout
        const duplicateCheck = await Promise.race([
          checkForDuplicates(lines, content),
          new Promise(resolve => setTimeout(() => resolve({ hasDuplicates: false, duplicateCount: 0, timeout: true }), 10000))
        ]) as any;
        
        if (duplicateCheck.timeout) {
          Alert.alert(
            'Duplicate Check Timeout',
            'Duplicate checking is taking too long. Would you like to skip duplicate checking and proceed with import?',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
              { text: 'Skip & Continue', onPress: () => processImport(lines, content) }
            ]
          );
          return;
        }
        
        if (duplicateCheck.shouldCancel) {
          resetImport();
          return;
        }
        
        if (duplicateCheck.hasDuplicates && !duplicateCheck.selectiveImport) {
          Alert.alert(
            'Duplicate Data Detected',
            `Found ${duplicateCheck.duplicateCount} potential duplicate records. Continue with import?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resetImport() },
              { text: 'Continue', onPress: () => processImport(lines, content, duplicateCheck) }
            ]
          );
          return;
        }
      }

      await processImport(lines, content, duplicateCheck);

    } catch (error) {
      console.error('Import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 5,
        message: 'Import failed',
        details: error.message || 'An unexpected error occurred',
        errors: [error.message || 'Unknown error']
      });
    }
  };

  const checkForDuplicates = async (lines: string[], fileContent: string) => {
    try {
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Checking for duplicates...',
        details: 'Level 1: Calculating file hash for instant duplicate detection'
      });

      // Calculate file hash for instant duplicate detection
      const fileHash = await calculateFileHash(fileContent);
      
      // Extract date range from file data
      const dates = lines.slice(1)
        .map(line => {
          const cells = line.split(',');
          // For Uber files, we'll use the current date as we don't have date columns
          // In a real scenario, you'd extract this from the data or filename
          return new Date().toISOString().split('T')[0];
        })
        .filter(date => date);
      
      const startDate = dates.length > 0 ? dates[0] : new Date().toISOString().split('T')[0];
      const endDate = dates.length > 0 ? dates[dates.length - 1] : new Date().toISOString().split('T')[0];

      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Checking for duplicates...',
        details: 'Level 2: Checking for exact file duplicates using hash'
      });

      // Level 1: Check for exact file duplicate
      const { data: fileDuplicateCheck, error: fileError } = await supabase
        .rpc('check_uber_file_duplicate', {
          p_file_hash: fileHash,
          p_start_date: startDate,
          p_end_date: endDate
        });

      if (fileError) {
        console.error('Error checking file duplicates:', fileError);
      } else if (fileDuplicateCheck && fileDuplicateCheck.length > 0) {
        const duplicateInfo = fileDuplicateCheck[0];
        if (duplicateInfo.is_duplicate) {
          // Show alert with options for exact duplicate
          return new Promise((resolve) => {
            Alert.alert(
              "Exact File Duplicate Detected! 📁",
              `This exact file was already imported on ${new Date(duplicateInfo.import_date).toLocaleDateString()}\n\n` +
              `📊 Original Import Details:\n` +
              `• Batch ID: ${duplicateInfo.existing_batch_id}\n` +
              `• Records: ${duplicateInfo.record_count}\n` +
              `• File: ${duplicateInfo.existing_file || 'Unknown'}\n\n` +
              `What would you like to do?`,
              [
                {
                  text: "❌ Cancel Import",
                  style: "cancel",
                  onPress: () => resolve({ 
                    hasDuplicates: true, 
                    duplicateCount: duplicateInfo.record_count || 0,
                    shouldCancel: true 
                  })
                },
                {
                  text: "🔄 Force Re-import",
                  onPress: () => resolve({ 
                    hasDuplicates: true, 
                    duplicateCount: 0, // Allow import but mark as duplicate
                    fileHash,
                    isDuplicateFile: true,
                    originalBatchId: duplicateInfo.existing_batch_id
                  })
                },
                {
                  text: "🎯 Check for New Drivers",
                  onPress: async () => {
                    // Continue to Level 2 checking for selective import
                    const driverAnalysis = await performDriverAnalysis(lines, startDate, endDate);
                    resolve({
                      hasDuplicates: false,
                      duplicateCount: 0,
                      fileHash,
                      selectiveImportAvailable: driverAnalysis.newDrivers.length > 0,
                      driverAnalysis
                    });
                  }
                }
              ],
              { cancelable: false }
            );
          });
        }
      }

      // Level 2: Analyze drivers for selective import opportunity
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Checking for duplicates...',
        details: 'Level 3: Analyzing driver data for selective import'
      });

      const driverAnalysis = await performDriverAnalysis(lines, startDate, endDate);

      if (driverAnalysis.existingDrivers.length > 0 && driverAnalysis.newDrivers.length > 0) {
        // Show selective import option
        return new Promise((resolve) => {
          Alert.alert(
            "Mixed Driver Data Detected 🎯",
            `Found both existing and new drivers in this file:\n\n` +
            `🟢 NEW drivers: ${driverAnalysis.newDrivers.length}\n` +
            `🟡 EXISTING drivers: ${driverAnalysis.existingDrivers.length}\n\n` +
            `Would you like to import only the new drivers?`,
            [
              {
                text: "❌ Cancel Import",
                style: "cancel",
                onPress: () => resolve({ 
                  hasDuplicates: true, 
                  duplicateCount: driverAnalysis.existingDrivers.length,
                  shouldCancel: true 
                })
              },
              {
                text: "📥 Import All",
                onPress: () => resolve({ 
                  hasDuplicates: false, 
                  duplicateCount: 0,
                  fileHash,
                  driverAnalysis
                })
              },
              {
                text: "🎯 Import New Only",
                onPress: () => resolve({
                  hasDuplicates: false,
                  duplicateCount: 0,
                  fileHash,
                  selectiveImport: true,
                  driverAnalysis
                })
              }
            ],
            { cancelable: false }
          );
        });
      }

      // Level 3: Quick amount-based duplicate check (fallback)
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 5, 
        message: 'Checking for duplicates...',
        details: 'Level 4: Quick amount-based duplicate checking'
      });

      const amountDuplicates = await checkAmountDuplicates(lines);

      return {
        hasDuplicates: amountDuplicates.duplicateCount > 0,
        duplicateCount: amountDuplicates.duplicateCount,
        fileHash,
        driverAnalysis
      };

    } catch (error) {
      console.error('Error in enhanced duplicate checking:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  const performDriverAnalysis = async (lines: string[], startDate: string, endDate: string) => {
    try {
      // Extract all driver UUIDs from the file
      const driverUuids = lines.slice(1)
        .map(line => {
          const cells = line.split(',');
          return cells[0]?.trim();
        })
        .filter(uuid => uuid && uuid.length > 0);

      // Remove duplicates
      const uniqueUuids = [...new Set(driverUuids)];

      // Check which drivers are new
      const { data: driverAnalysis, error } = await supabase
        .rpc('find_new_uber_drivers', {
          p_driver_uuids: uniqueUuids,
          p_start_date: startDate,
          p_end_date: endDate
        });

      if (error) {
        console.error('Error analyzing drivers:', error);
        return { newDrivers: [], existingDrivers: [], allDrivers: uniqueUuids };
      }

      const newDrivers = driverAnalysis
        ?.filter(d => d.recommendation === 'NEW - Safe to import')
        ?.map(d => d.new_driver_uuid) || [];

      const existingDrivers = driverAnalysis
        ?.filter(d => d.recommendation === 'DUPLICATE - Consider selective import')
        ?.map(d => d.new_driver_uuid) || [];

      return {
        newDrivers,
        existingDrivers,
        allDrivers: uniqueUuids
      };
    } catch (error) {
      console.error('Error in driver analysis:', error);
      return { newDrivers: [], existingDrivers: [], allDrivers: [] };
    }
  };

  const checkAmountDuplicates = async (lines: string[]) => {
    try {
      // Get existing transaction amounts for Uber (limit for performance)
      const { data: existingTransactions, error } = await supabase
        .from('accounting_transactions')
        .select('transaction_date, amount, driver_id')
        .eq('category', 'uber')
        .limit(1000);

      if (error) {
        console.error('Error fetching existing transactions:', error);
        return { duplicateCount: 0 };
      }

      let duplicateCount = 0;
      const existingDateAmounts = new Set();
      
      if (existingTransactions && existingTransactions.length > 0) {
        existingTransactions.forEach(tx => {
          existingDateAmounts.add(`${tx.transaction_date}-${tx.amount}`);
        });
      }

      // Quick check: only examine first 100 records for duplicates to avoid hanging
      const recordsToCheck = Math.min(lines.length - 1, 100);

      for (let i = 1; i <= recordsToCheck; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;

        const cells = line.split(',');
        const totalIncome = parseFloat(cells[3] || '0');
        
        if (totalIncome > 0) {
          // Use a standard date for checking (in real scenario, extract from data)
          const checkKey = `${new Date().toISOString().split('T')[0]}-${totalIncome}`;
          
          if (existingDateAmounts.has(checkKey)) {
            duplicateCount++;
          }
        }
      }

      return { duplicateCount };
    } catch (error) {
      console.error('Error checking amount duplicates:', error);
      return { duplicateCount: 0 };
    }
  };

  const processImport = async (lines: string[], fileContent: string = '', duplicateCheckResult: any = {}) => {
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 5, 
      message: duplicateCheckResult.selectiveImport ? 'Processing new drivers only...' : 'Matching drivers using UUIDs...',
      details: duplicateCheckResult.selectiveImport ? 'Importing only new drivers based on analysis' : 'Comparing UUIDs with database'
    });

    let totalEarnings = 0;
    let processedCount = 0;
    let uuidMatches = 0;
    let nameMatches = 0;
    let notFoundCount = 0;
    let rawRecordsSaved = 0;
    const errors: string[] = [];

    // Generate batch ID and metadata
    const batchId = crypto.randomUUID ? crypto.randomUUID() : `batch_${Date.now()}`;
    const currentDate = new Date().toISOString().split('T')[0];
    const importTimestamp = new Date().toISOString();
    const sourceFile = selectedFile?.name || 'unknown.csv';
    
    // Determine date range from file data or use current date
    const dates = lines.slice(1)
      .map(line => {
        const cells = line.split(',');
        // In a real scenario, extract date from data or filename
        return currentDate;
      })
      .filter(date => date);
    
    const startDate = dates.length > 0 ? dates[0] : currentDate;
    const endDate = dates.length > 0 ? dates[dates.length - 1] : currentDate;

    // Get list of drivers to process (all or selective)
    const driversToProcess = duplicateCheckResult.selectiveImport && duplicateCheckResult.driverAnalysis
      ? new Set(duplicateCheckResult.driverAnalysis.newDrivers)
      : null;

    // Process each data row (skip header)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const cells = line.split(',');
      const uberUuid = cells[0] || '';
      const firstName = cells[1] || '';
      const lastName = cells[2] || '';
      const totalIncome = parseFloat(cells[3] || '0');
      
      const fullName = `${firstName} ${lastName}`.trim();

      // Skip this record if selective import and driver not in new list
      if (driversToProcess && !driversToProcess.has(uberUuid)) {
        continue;
      }

      if (totalIncome > 0 && fullName !== '') {
        try {
          // Save to raw table first
          const { error: rawError } = await supabase
            .from('uber_income_raw')
            .insert({
              uber_driver_uuid: uberUuid,
              first_name: firstName,
              last_name: lastName,
              total_income: totalIncome,
              import_batch_id: batchId,
              batch_start_date: startDate,
              batch_end_date: endDate,
              import_timestamp: importTimestamp,
              records_in_batch: lines.length - 1,
              file_hash: duplicateCheckResult.fileHash || null,
              is_duplicate: duplicateCheckResult.isDuplicateFile || false,
              duplicate_of_batch: duplicateCheckResult.originalBatchId || null,
              selective_import: duplicateCheckResult.selectiveImport || false,
              source_file: sourceFile
            });

          if (rawError) {
            console.error('Error saving raw data:', rawError);
          } else {
            rawRecordsSaved++;
          }

          const result = await findUberDriverByUuid(uberUuid, fullName);
          
          if (result.driver) {
            // Track match types
            if (result.matchType === 'uuid') {
              uuidMatches++;
            } else if (result.matchType === 'name') {
              nameMatches++;
            }
            
            // Create transaction record
            const { error: insertError } = await supabase
              .from('accounting_transactions')
              .insert({
                transaction_date: currentDate,
                type: 'earning',
                category: 'uber',
                amount: totalIncome,
                description: `Uber driver payment: ${fullName}`,
                driver_id: result.driver.id,
                import_batch_id: batchId,
                batch_start_date: startDate,
                batch_end_date: endDate,
                source_filename: sourceFile,
                import_timestamp: importTimestamp,
                records_in_batch: lines.length - 1,
                created_at: new Date().toISOString()
              });

            if (insertError) {
              errors.push(`Error inserting transaction for ${fullName}: ${insertError.message}`);
            } else {
              totalEarnings += totalIncome;
              processedCount++;
            }
          } else {
            notFoundCount++;
            errors.push(`Driver not found: ${fullName} (UUID: ${uberUuid.substring(0, 8)}...)`);
          }
        } catch (error) {
          errors.push(`Error processing ${fullName}: ${error.message}`);
        }
      }

      // Update progress
      if (i % 25 === 0) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 4, 
          totalSteps: 5, 
          message: duplicateCheckResult.selectiveImport ? 'Processing new drivers only...' : 'Processing transactions...',
          details: duplicateCheckResult.selectiveImport 
            ? `Processed ${processedCount} new drivers`
            : `Processed ${i} of ${lines.length - 1} records`,
          processed: i,
          total: lines.length - 1
        });
      }
    }

    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 5, 
      message: 'Finalizing import...',
      details: 'Calculating final results'
    });

    // Set final results
    const stats: ImportStats = {
      totalRecords: lines.length - 1,
      uuidMatches: uuidMatches,
      nameMatches: nameMatches,
      notFound: notFoundCount,
      duplicates: duplicateCheckResult.duplicateCount || 0,
      totalAmount: totalEarnings,
      accuracy: lines.length > 1 ? Math.round(((uuidMatches + nameMatches) / (lines.length - 1)) * 100) : 0,
      dateRange: startDate === endDate ? startDate : `${startDate} to ${endDate}`,
      batchId: batchId,
      sourceFile: sourceFile,
      importType: duplicateCheckResult.selectiveImport ? 'Selective (New Drivers Only)' : 'Standard',
      fileHash: duplicateCheckResult.fileHash,
      processedRecords: processedCount,
      rawRecordsSaved: rawRecordsSaved
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 5, 
      totalSteps: 5, 
      message: duplicateCheckResult.selectiveImport 
        ? `${stats.importType} completed successfully!`
        : 'Import completed successfully!',
      details: duplicateCheckResult.selectiveImport
        ? `${stats.processedRecords} new drivers imported out of ${stats.totalRecords} total records`
        : `${stats.uuidMatches + stats.nameMatches} transactions imported, ${stats.rawRecordsSaved} raw records saved`,
      errors: errors.length > 0 ? errors : undefined
    });
  };

  const findUberDriverByUuid = async (uberUuid: string, fullName: string) => {
    try {
      // First try UUID match
      if (uberUuid && uberUuid.trim() !== '') {
        const { data: driverByUuid, error: uuidError } = await supabase
          .from('drivers')
          .select('id, name, uber_uuid')
          .eq('uber_uuid', uberUuid.trim());
        
        if (driverByUuid && driverByUuid.length > 0 && !uuidError) {
          return { driver: driverByUuid[0], matchType: 'uuid' };
        }
      }
      
      // Fallback to name matching
      if (fullName && fullName.trim() !== '') {
        const { data: driversByName, error: nameError } = await supabase
          .from('drivers')
          .select('id, name, uber_uuid')
          .ilike('name', `%${fullName.trim()}%`);
        
        if (driversByName && driversByName.length > 0 && !nameError) {
          return { driver: driversByName[0], matchType: 'name' };
        }
      }
      
      return { driver: null, matchType: 'none' };
    } catch (error) {
      console.error(`Error finding Uber driver ${fullName}:`, error);
      return { driver: null, matchType: 'error' };
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 5,
      message: 'Ready to import Uber data',
    });
    setImportStats(null);
    setShowDetails(false);
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return theme.colors.primary;
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Uber Import" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Ionicons name="car" size={24} color={theme.colors.primary} />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Uber Data Import</Text>
              <Text style={styles.subtitle}>Import driver earnings and transaction data from Uber CSV</Text>
            </View>
          </View>

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity style={styles.filePickerButton} onPress={pickFile}>
              <Ionicons name="cloud-upload" size={20} color={theme.colors.primary} />
              <Text style={styles.filePickerText}>
                {selectedFile ? selectedFile.name : 'Choose Uber CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={styles.progressCard}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {progress.phase === 'processing' && (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { width: `${(progress.processed / progress.total) * 100}%` }
                      ]} 
                    />
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Statistics */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.success }]}>
                    {importStats.uuidMatches}
                  </Text>
                  <Text style={styles.statLabel}>UUID Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.nameMatches}
                  </Text>
                  <Text style={styles.statLabel}>Name Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                {importStats.dateRange && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Date Range:</Text>
                    <Text style={styles.summaryValue}>{importStats.dateRange}</Text>
                  </View>
                )}
                {importStats.importType && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Import Type:</Text>
                    <Text style={[styles.summaryValue, { 
                      color: importStats.importType.includes('Selective') ? theme.colors.warning : theme.colors.text 
                    }]}>
                      {importStats.importType}
                    </Text>
                  </View>
                )}
                {importStats.rawRecordsSaved !== undefined && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Raw Records Saved:</Text>
                    <Text style={styles.summaryValue}>{importStats.rawRecordsSaved}</Text>
                  </View>
                )}
                {importStats.batchId && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Batch ID:</Text>
                    <Text style={[styles.summaryValue, { fontSize: 12, fontFamily: 'monospace' }]}>
                      {importStats.batchId.substring(0, 8)}...
                    </Text>
                  </View>
                )}
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Import Date:</Text>
                  <Text style={styles.summaryValue}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {progress.phase === 'idle' || progress.phase === 'complete' ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performRealImport(false)}
                  disabled={!selectedFile || progress.phase === 'processing'}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    {progress.phase === 'complete' ? 'Import Another File' : 'Import with Duplicate Check'}
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performRealImport(true)}
                  disabled={!selectedFile || progress.phase === 'processing'}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicate Check)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity style={styles.secondaryButton} onPress={resetImport}>
                    <Ionicons name="refresh" size={20} color={theme.colors.primary} />
                    <Text style={styles.secondaryButtonText}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <TouchableOpacity style={styles.disabledButton} disabled>
                <ActivityIndicator size="small" color={theme.colors.white} />
                <Text style={styles.primaryButtonText}>Processing...</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.primary + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.primary,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.primary,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.primary,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
});
